<script setup>
import { ref, computed, watch, onMounted, nextTick } from 'vue';
import { format, formatDistanceToNow, isValid } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import KanbanAPI from '../../../../api/kanban';
import { emitter } from 'shared/helpers/mitt';

const props = defineProps({
  item: {
    type: Object,
    required: true,
  },
});

const emit = defineEmits(['item-updated']);

// Estado local
const saving = ref(false);
const hasChanges = ref(false);
const isInitializing = ref(true); // [FIX] Bloqueia watchers durante inicialização

// Dados do agendamento
const isSchedulingEnabled = ref(false);
const schedulingType = ref('deadline'); // 'deadline' ou 'schedule'
const scheduledDate = ref('');
const scheduledTime = ref('');
const deadlineDate = ref('');

// Histórico de remarcações
const reschedulingHistory = computed(() => {
  return props.item?.item_details?.rescheduling_history || [];
});

// Computed para verificar se foi remarcado
const wasRescheduled = computed(() => {
  return props.item?.item_details?.rescheduled || reschedulingHistory.value.length > 0;
});

// Carregar dados do agendamento
const loadSchedulingData = async () => {
  isInitializing.value = true; // [FIX] Bloqueia watchers
  
  const itemDetails = props.item?.item_details || {};
  
  // Verificar se há agendamento
  const hasScheduledAt = !!itemDetails.scheduled_at;
  const hasDeadlineAt = !!itemDetails.deadline_at;
  
  isSchedulingEnabled.value = hasScheduledAt || hasDeadlineAt;
  
  if (hasScheduledAt) {
    schedulingType.value = 'schedule';
    const date = new Date(itemDetails.scheduled_at);
    // [FIX] Validar se a data é válida antes de formatar
    if (isValid(date)) {
      scheduledDate.value = format(date, 'yyyy-MM-dd');
      scheduledTime.value = format(date, 'HH:mm');
    } else {
      console.warn('[KanbanSchedulingTab] scheduled_at inválido:', itemDetails.scheduled_at);
      scheduledDate.value = '';
      scheduledTime.value = '';
    }
  } else if (hasDeadlineAt) {
    schedulingType.value = 'deadline';
    const date = new Date(itemDetails.deadline_at);
    // [FIX] Validar se a data é válida antes de formatar
    if (isValid(date)) {
      deadlineDate.value = format(date, 'yyyy-MM-dd');
    } else {
      console.warn('[KanbanSchedulingTab] deadline_at inválido:', itemDetails.deadline_at);
      deadlineDate.value = '';
    }
  } else {
    // Reset campos quando não há agendamento
    scheduledDate.value = '';
    scheduledTime.value = '';
    deadlineDate.value = '';
  }
  
  // [FIX] Aguarda próximo tick e desbloqueia watchers
  await nextTick();
  hasChanges.value = false;
  isInitializing.value = false;
};

// [FIX] Helper para validar e parsear data
const parseDate = (dateString) => {
  if (!dateString) return null;
  const date = new Date(dateString);
  return isValid(date) ? date : null;
};

// Formatar data para exibição
const formatDisplayDate = (dateString) => {
  const date = parseDate(dateString);
  if (!date) return '';
  try {
    return format(date, "dd 'de' MMMM 'de' yyyy", { locale: ptBR });
  } catch {
    return dateString || '';
  }
};

// Formatar data e hora para exibição
const formatDisplayDateTime = (dateString) => {
  const date = parseDate(dateString);
  if (!date) return '';
  try {
    return format(date, "dd 'de' MMMM 'de' yyyy 'às' HH:mm", { locale: ptBR });
  } catch {
    return dateString || '';
  }
};

// Formatar distância relativa
const formatRelativeDate = (dateString) => {
  const date = parseDate(dateString);
  if (!date) return '';
  try {
    return formatDistanceToNow(date, { locale: ptBR, addSuffix: true });
  } catch {
    return '';
  }
};

// Computed para exibição do agendamento atual
const currentScheduleDisplay = computed(() => {
  const itemDetails = props.item?.item_details || {};
  
  if (itemDetails.scheduled_at) {
    return {
      type: 'schedule',
      label: 'Agendamento',
      date: formatDisplayDateTime(itemDetails.scheduled_at),
      relative: formatRelativeDate(itemDetails.scheduled_at),
    };
  }
  
  if (itemDetails.deadline_at) {
    return {
      type: 'deadline',
      label: 'Prazo',
      date: formatDisplayDate(itemDetails.deadline_at),
      relative: formatRelativeDate(itemDetails.deadline_at),
    };
  }
  
  return null;
});

// Watchers para detectar mudanças - [FIX] Bloqueia durante inicialização
watch(isSchedulingEnabled, () => {
  if (!isInitializing.value) hasChanges.value = true;
});

watch(schedulingType, () => {
  if (!isInitializing.value) hasChanges.value = true;
});

watch(scheduledDate, () => {
  if (!isInitializing.value) hasChanges.value = true;
});

watch(scheduledTime, () => {
  if (!isInitializing.value) hasChanges.value = true;
});

watch(deadlineDate, () => {
  if (!isInitializing.value) hasChanges.value = true;
});

// Watch para recarregar quando o item mudar
watch(() => props.item?.id, () => {
  loadSchedulingData();
}, { immediate: false });

// Salvar alterações
const saveChanges = async () => {
  if (!props.item?.id) {
    console.error('saveChanges: props.item ou props.item.id não definido');
    emitter.emit('newToastMessage', {
      message: 'Erro interno: item não identificado',
      action: { type: 'error' },
    });
    return;
  }

  // [FIX] Validar se há data selecionada quando agendamento está ativo
  if (isSchedulingEnabled.value) {
    if (schedulingType.value === 'schedule' && !scheduledDate.value) {
      emitter.emit('newToastMessage', {
        message: 'Selecione uma data para o agendamento',
        action: { type: 'error' },
      });
      return;
    }
    if (schedulingType.value === 'deadline' && !deadlineDate.value) {
      emitter.emit('newToastMessage', {
        message: 'Selecione uma data para o prazo',
        action: { type: 'error' },
      });
      return;
    }
  }

  try {
    saving.value = true;

    const currentItemDetails = props.item.item_details || {};
    
    // Preparar dados do agendamento
    let scheduled_at = null;
    let deadline_at = null;
    let scheduling_type = null;
    let rescheduled = currentItemDetails.rescheduled || false;
    let rescheduling_history = currentItemDetails.rescheduling_history || [];
    
    if (isSchedulingEnabled.value) {
      scheduling_type = schedulingType.value;
      
      if (schedulingType.value === 'schedule' && scheduledDate.value) {
        const dateTime = scheduledTime.value 
          ? `${scheduledDate.value}T${scheduledTime.value}:00`
          : `${scheduledDate.value}T00:00:00`;
        scheduled_at = new Date(dateTime).toISOString();
        
        // Verificar se é remarcação
        if (currentItemDetails.scheduled_at && currentItemDetails.scheduled_at !== scheduled_at) {
          rescheduled = true;
          rescheduling_history = [
            ...rescheduling_history,
            {
              from_date: currentItemDetails.scheduled_at,
              to_date: scheduled_at,
              changed_at: new Date().toISOString(),
            },
          ];
        }
      } else if (schedulingType.value === 'deadline' && deadlineDate.value) {
        deadline_at = deadlineDate.value;
        
        // Verificar se é remarcação
        if (currentItemDetails.deadline_at && currentItemDetails.deadline_at !== deadline_at) {
          rescheduled = true;
          rescheduling_history = [
            ...rescheduling_history,
            {
              from_date: currentItemDetails.deadline_at,
              to_date: deadline_at,
              changed_at: new Date().toISOString(),
            },
          ];
        }
      }
    }

    // [FIX] Payload completo seguindo padrão de KanbanOffersTab
    const payload = {
      funnel_id: props.item.funnel_id,
      funnel_stage: props.item.funnel_stage,
      position: props.item.position,
      item_details: {
        ...currentItemDetails,
        scheduled_at,
        deadline_at,
        scheduling_type,
        rescheduled,
        rescheduling_history,
      },
      custom_attributes: props.item.custom_attributes || {},
    };

    await KanbanAPI.updateItem(props.item.id, payload);

    emitter.emit('newToastMessage', {
      message: 'Agendamento salvo com sucesso',
      action: { type: 'success' },
    });

    hasChanges.value = false;
    emit('item-updated');
  } catch (error) {
    console.error('Erro ao salvar agendamento:', error);
    const errorMessage = error?.response?.data?.message || error?.message || 'Erro desconhecido';
    emitter.emit('newToastMessage', {
      message: `Erro ao salvar agendamento: ${errorMessage}`,
      action: { type: 'error' },
    });
  } finally {
    saving.value = false;
  }
};

// Cancelar alterações
const cancelChanges = () => {
  loadSchedulingData();
};

// Limpar agendamento
const clearScheduling = () => {
  isSchedulingEnabled.value = false;
  scheduledDate.value = '';
  scheduledTime.value = '';
  deadlineDate.value = '';
  hasChanges.value = true;
};

// Lifecycle
onMounted(() => {
  loadSchedulingData();
});
</script>

<template>
  <div class="kanban-scheduling-tab p-4">
    <!-- Header com botões de ação -->
    <div class="flex items-center justify-between mb-6">
      <div class="flex items-center gap-2">
        <div class="w-8 h-8 rounded-lg bg-blue-50 dark:bg-blue-900/20 flex items-center justify-center">
          <fluent-icon icon="calendar" size="18" class="text-blue-500" />
        </div>
        <h3 class="text-base font-semibold text-slate-800 dark:text-slate-200">
          Agendamento
        </h3>
      </div>

      <!-- Botões de ação -->
      <div v-if="hasChanges" class="flex items-center gap-2">
        <button
          type="button"
          class="px-3 py-1.5 text-xs font-medium text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
          :disabled="saving"
          @click="cancelChanges"
        >
          Cancelar
        </button>
        <button
          type="button"
          class="px-3 py-1.5 text-xs font-medium text-white bg-woot-600 hover:bg-woot-700 rounded-lg transition-colors flex items-center gap-1.5"
          :disabled="saving"
          @click="saveChanges"
        >
          <span v-if="saving" class="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
          {{ saving ? 'Salvando...' : 'Salvar' }}
        </button>
      </div>
    </div>

    <!-- Exibição do agendamento atual (se existir e não estiver editando) -->
    <div v-if="currentScheduleDisplay && !hasChanges" class="mb-6">
      <div class="p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl">
        <div class="flex items-start gap-3">
          <div class="flex-shrink-0 w-10 h-10 rounded-lg bg-blue-100 dark:bg-blue-900/40 flex items-center justify-center">
            <fluent-icon 
              :icon="currentScheduleDisplay.type === 'schedule' ? 'calendar-clock' : 'calendar'" 
              size="20" 
              class="text-blue-600 dark:text-blue-400" 
            />
          </div>
          <div class="flex-1">
            <span class="text-xs font-medium text-blue-600 dark:text-blue-400 uppercase tracking-wide">
              {{ currentScheduleDisplay.label }}
            </span>
            <p class="text-sm font-semibold text-slate-800 dark:text-slate-200 mt-0.5">
              {{ currentScheduleDisplay.date }}
            </p>
            <p class="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
              {{ currentScheduleDisplay.relative }}
            </p>
          </div>
        </div>
        
        <!-- Badge de remarcação -->
        <div v-if="wasRescheduled" class="mt-3 pt-3 border-t border-blue-200 dark:border-blue-800">
          <span class="inline-flex items-center gap-1 text-xs text-orange-600 dark:text-orange-400">
            <fluent-icon icon="arrow-sync" size="12" />
            Remarcado {{ reschedulingHistory.length }} vez(es)
          </span>
        </div>
      </div>
    </div>

    <!-- Toggle para ativar agendamento -->
    <div class="space-y-4">
      <div class="flex items-center gap-3 p-3 bg-slate-50 dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700">
        <input
          id="scheduling-enabled"
          v-model="isSchedulingEnabled"
          type="checkbox"
          class="w-4 h-4 text-woot-500 border-slate-300 rounded focus:ring-woot-500"
        />
        <label for="scheduling-enabled" class="text-sm font-medium text-slate-700 dark:text-slate-300 cursor-pointer">
          Agendar este item
        </label>
      </div>

      <!-- Opções de agendamento -->
      <div v-if="isSchedulingEnabled" class="space-y-4 animate-fade-in">
        <!-- Tipo de agendamento -->
        <div class="flex gap-4 p-3 bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700">
          <label class="flex items-center gap-2 cursor-pointer">
            <input
              v-model="schedulingType"
              type="radio"
              value="deadline"
              class="w-4 h-4 text-woot-500 border-slate-300 focus:ring-woot-500"
            />
            <span class="text-sm text-slate-700 dark:text-slate-300">Prazo</span>
          </label>
          <label class="flex items-center gap-2 cursor-pointer">
            <input
              v-model="schedulingType"
              type="radio"
              value="schedule"
              class="w-4 h-4 text-woot-500 border-slate-300 focus:ring-woot-500"
            />
            <span class="text-sm text-slate-700 dark:text-slate-300">Agendamento</span>
          </label>
        </div>

        <!-- Campo de data (Prazo) -->
        <div v-if="schedulingType === 'deadline'" class="space-y-2">
          <label class="block text-sm font-medium text-slate-700 dark:text-slate-300">
            Data do Prazo
          </label>
          <input
            v-model="deadlineDate"
            type="date"
            class="w-full px-3 py-2 text-sm bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg focus:ring-2 focus:ring-woot-500/20 focus:border-woot-500"
          />
        </div>

        <!-- Campos de data e hora (Agendamento) -->
        <div v-else class="space-y-3">
          <div class="space-y-2">
            <label class="block text-sm font-medium text-slate-700 dark:text-slate-300">
              Data do Agendamento
            </label>
            <input
              v-model="scheduledDate"
              type="date"
              class="w-full px-3 py-2 text-sm bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg focus:ring-2 focus:ring-woot-500/20 focus:border-woot-500"
            />
          </div>
          <div class="space-y-2">
            <label class="block text-sm font-medium text-slate-700 dark:text-slate-300">
              Horário
            </label>
            <input
              v-model="scheduledTime"
              type="time"
              class="w-full px-3 py-2 text-sm bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg focus:ring-2 focus:ring-woot-500/20 focus:border-woot-500"
            />
          </div>
        </div>

        <!-- Botão limpar -->
        <button
          v-if="currentScheduleDisplay"
          type="button"
          class="w-full px-3 py-2 text-xs font-medium text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg transition-colors"
          @click="clearScheduling"
        >
          Remover Agendamento
        </button>
      </div>

      <!-- Estado vazio -->
      <div
        v-if="!isSchedulingEnabled && !currentScheduleDisplay"
        class="flex flex-col items-center justify-center py-8 text-center"
      >
        <div class="w-12 h-12 mb-3 rounded-full bg-slate-100 dark:bg-slate-700 flex items-center justify-center">
          <fluent-icon icon="calendar" size="24" class="text-slate-400" />
        </div>
        <p class="text-sm text-slate-600 dark:text-slate-400 mb-1">
          Nenhum agendamento definido
        </p>
        <p class="text-xs text-slate-500 dark:text-slate-500">
          Ative o agendamento para definir prazos ou datas
        </p>
      </div>
    </div>

    <!-- Histórico de remarcações -->
    <div v-if="reschedulingHistory.length > 0" class="mt-6 pt-4 border-t border-slate-200 dark:border-slate-700">
      <h4 class="text-xs font-semibold text-slate-600 dark:text-slate-400 uppercase tracking-wide mb-3">
        Histórico de Remarcações
      </h4>
      <div class="space-y-2">
        <div
          v-for="(history, index) in reschedulingHistory"
          :key="index"
          class="flex items-center gap-2 p-2 bg-slate-50 dark:bg-slate-800 rounded-lg text-xs"
        >
          <fluent-icon icon="arrow-right" size="12" class="text-slate-400" />
          <span class="text-slate-600 dark:text-slate-400">
            {{ formatDisplayDate(history.from_date) }}
          </span>
          <fluent-icon icon="arrow-right" size="10" class="text-slate-400" />
          <span class="text-slate-800 dark:text-slate-200 font-medium">
            {{ formatDisplayDate(history.to_date) }}
          </span>
          <span class="ml-auto text-slate-400">
            {{ formatRelativeDate(history.changed_at) }}
          </span>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.animate-fade-in {
  animation: fadeIn 0.2s ease-out;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(-4px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
